import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Grid, Divider, IconButton,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GroupsIcon from '@mui/icons-material/Groups';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend,
} from 'recharts';

const STAGES = [
  { key: 'identified',  label: 'Identifierad',   color: '#78909C' },
  { key: 'qualified',   label: 'Kvalificerad',    color: '#1565C0' },
  { key: 'demo',        label: 'Demo',            color: '#00897B' },
  { key: 'sandbox',     label: 'Sandbox',         color: '#F57C00' },
  { key: 'proposal',    label: 'Offert skickad',  color: '#FF6B00' },
  { key: 'negotiation', label: 'Förhandling',     color: '#6A1B9A' },
  { key: 'pilot',       label: 'Pilot',           color: '#3949AB' },
  { key: 'won',         label: 'Vunnen',          color: '#2E7D32' },
  { key: 'lost',        label: 'Förlorad',        color: '#C62828' },
  { key: 'dormant',     label: 'Vilande',         color: '#795548' },
];

const ACTIVITY_COLORS = {
  email: '#1565C0', phone: '#2E7D32', meeting: '#6A1B9A',
  quote: '#E65100', note: '#FF6B00',
};
const ACTIVITY_LABELS = {
  email: 'E-post', phone: 'Samtal', meeting: 'Möte',
  quote: 'Offert', note: 'Notering',
};

function svMonth(date) {
  return date.toLocaleString('sv-SE', { month: 'short' }) + ' ' + String(date.getFullYear()).slice(2);
}

function formatSEK(val) {
  if (!val) return '0 KSEK';
  return Number(val).toLocaleString('sv-SE') + ' KSEK';
}

// Custom radial-style "gauge" using pure CSS
function GaugeCard({ label, value, max, color, formatter, icon, subtitle }) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const strokeDasharray = 2 * Math.PI * 40;
  const strokeDashoffset = strokeDasharray * (1 - pct * 0.75);
  return (
    <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 2 }}>
        {label}
      </Typography>
      <Box sx={{ position: 'relative', width: 110, height: 80, mx: 'auto', mb: 1 }}>
        <svg viewBox="0 0 100 60" width="110" height="66">
          {/* Track */}
          <path d="M 10 55 A 40 40 0 0 1 90 55" fill="none" stroke="#e0e0e0" strokeWidth="8" strokeLinecap="round" />
          {/* Fill */}
          <path d="M 10 55 A 40 40 0 0 1 90 55" fill="none" stroke={color}
            strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${strokeDasharray}`}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 1s ease', transformOrigin: '50px 55px', transform: 'rotate(0deg)' }} />
          <text x="50" y="48" textAnchor="middle" fontSize="15" fontWeight="bold" fill={color}>
            {formatter ? formatter(value) : value}
          </text>
        </svg>
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      )}
    </Paper>
  );
}

// Big stat card
function BigStatCard({ label, value, icon, color, sub, trend }) {
  return (
    <Paper sx={{ p: 3, borderTop: `4px solid ${color}`, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight="bold"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight="900" sx={{ color, lineHeight: 1 }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {sub}
            </Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: color + '18', p: 1.5, borderRadius: 2 }}>
          {icon}
        </Box>
      </Box>
      {trend != null && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrendingUpIcon sx={{ fontSize: 14, color: trend >= 0 ? '#2E7D32' : '#C62828' }} />
          <Typography variant="caption"
            sx={{ color: trend >= 0 ? '#2E7D32' : '#C62828', fontWeight: 600 }}>
            {trend >= 0 ? '+' : ''}{trend}% vs. förra månaden
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default function AffärerStatistik() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    async function loadAll() {
      const [leadsSnap, plingSnap, usersSnap] = await Promise.all([
        getDocs(query(collection(db, 'crmLeads'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'plingKlang'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'users')),
      ]);

      // Also load activities from all leads (sampled for speed — up to 200 leads)
      const leads = leadsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const plings = plingSnap.docs.map(d => d.data());
      const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Activities per lead (parallel, up to 50 leads)
      const activitiesList = await Promise.all(
        leads.slice(0, 50).map(l =>
          getDocs(collection(db, 'crmLeads', l.id, 'activities'))
            .then(s => s.docs.map(d => d.data()))
        )
      );
      const allActivities = activitiesList.flat();

      setData(processData(leads, plings, users, allActivities));
      setLoading(false);
    }
    loadAll();
  }, []);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}>
      <CircularProgress />
    </Box>
  );

  const {
    monthlyPling, pipelineFunnel, winRate, avgDealValue, pipelineByMonth,
    activityBreakdown, topOwners, totalPipeline, activeLeads, wonLeads, wonValue,
    thisMonthPling, thisMonthKling, conversionRate,
  } = data;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">Affärer – Statistik & KPI:er</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>Realtidsdata från pipeline</Typography>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* Top KPIs */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <BigStatCard label="Aktiv pipeline" value={formatSEK(totalPipeline)}
              color="#FF6B00" sub={`${activeLeads} leads`}
              icon={<TrendingUpIcon sx={{ color: '#FF6B00', fontSize: 28 }} />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <BigStatCard label="Vunna affärer" value={wonLeads}
              color="#2E7D32" sub={formatSEK(wonValue) + ' totalt'}
              icon={<EmojiEventsIcon sx={{ color: '#2E7D32', fontSize: 28 }} />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <BigStatCard label="Genomsnitt deal" value={formatSEK(avgDealValue)}
              color="#1565C0"
              icon={<BarChartIcon sx={{ color: '#1565C0', fontSize: 28 }} />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <BigStatCard label="PLING denna månad" value={thisMonthPling}
              color="#6A1B9A" sub={`${thisMonthKling} KLING-KLANG 🏆`}
              icon={<NotificationsActiveIcon sx={{ color: '#6A1B9A', fontSize: 28 }} />} />
          </Grid>
        </Grid>

        {/* Gauges row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <GaugeCard label="Win rate" value={winRate} max={100} color="#2E7D32"
              formatter={v => v + '%'}
              subtitle="Vunna av avslutade" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <GaugeCard label="Konverteringsgrad" value={conversionRate} max={100} color="#1565C0"
              formatter={v => v + '%'}
              subtitle="Leads som blivit affär" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <GaugeCard label="PLING idag" value={thisMonthPling} max={Math.max(thisMonthPling, 10)}
              color="#FF6B00" subtitle="Denna månad" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <GaugeCard label="Aktiva leads" value={activeLeads}
              max={Math.max(activeLeads, 20)} color="#6A1B9A"
              subtitle="I pipeline just nu" />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* PLING & KLING-KLANG per månad */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <NotificationsActiveIcon sx={{ color: '#FF6B00', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  PLING & KLING-KLANG — senaste 12 månader
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthlyPling} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RTooltip
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    formatter={(val, name) => [val, name === 'pling' ? '🔔 PLING' : '🏆 KLING-KLANG']}
                  />
                  <Legend formatter={val => val === 'pling' ? '🔔 PLING' : '🏆 KLING-KLANG'} />
                  <Bar dataKey="pling" fill="#FF6B00" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="kling" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Pipeline-tratt */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <TrendingUpIcon sx={{ color: '#1565C0', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Pipeline-tratt
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {pipelineFunnel.map((stage, i) => {
                  const max = pipelineFunnel[0]?.count || 1;
                  const width = Math.max((stage.count / max) * 100, 8);
                  return (
                    <Box key={stage.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                        <Typography variant="caption" sx={{ width: 110, flexShrink: 0, color: 'text.secondary' }}>
                          {stage.label}
                        </Typography>
                        <Box sx={{
                          height: 22, borderRadius: 1,
                          bgcolor: stage.color, width: `${width}%`,
                          display: 'flex', alignItems: 'center', px: 1,
                          minWidth: 28, transition: 'width 0.8s ease',
                        }}>
                          <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.65rem' }}>
                            {stage.count}
                          </Typography>
                        </Box>
                        {stage.value > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                            {formatSEK(stage.value)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid>

          {/* Pipeline-värde per månad */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <TrendingUpIcon sx={{ color: '#1565C0', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Vunnet affärsvärde — per månad
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={pipelineByMonth}>
                  <defs>
                    <linearGradient id="wonGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2E7D32" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2E7D32" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatSEK(v)} width={70} />
                  <RTooltip formatter={v => formatSEK(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="won" stroke="#2E7D32" strokeWidth={2}
                    fill="url(#wonGrad)" name="Vunnet värde" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Aktivitetstyper */}
          <Grid item xs={12} sm={6} lg={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <GroupsIcon sx={{ color: '#6A1B9A', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Aktiviteter
                </Typography>
              </Box>
              {activityBreakdown.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Inga aktiviteter än
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={activityBreakdown} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" outerRadius={70} innerRadius={35}
                      paddingAngle={2}>
                      {activityBreakdown.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(v, n) => [v, n]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Legend iconSize={10} formatter={(val) => (
                      <span style={{ fontSize: 11 }}>{val}</span>
                    )} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Toppresultat per person */}
          {topOwners.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                  <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Säljare — vunna affärer
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {topOwners.map((owner, i) => (
                    <Box key={owner.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        bgcolor: i === 0 ? '#FFD700' : i === 1 ? '#9E9E9E' : '#CD7F32',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.8rem', fontWeight: 700, flexShrink: 0
                      }}>
                        {i + 1}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{owner.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {owner.won} vunna
                          </Typography>
                          {owner.value > 0 && (
                            <Typography variant="caption" sx={{ color: '#2E7D32' }}>
                              {formatSEK(owner.value)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ width: 80, height: 6, bgcolor: '#f0f0f0', borderRadius: 3 }}>
                        <Box sx={{
                          height: '100%', borderRadius: 3, bgcolor: '#2E7D32',
                          width: `${(owner.won / topOwners[0].won) * 100}%`
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Säljtips */}
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 3, bgcolor: '#1a1a1a', color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography sx={{ fontSize: '1.3rem' }}>💡</Typography>
                <Typography variant="subtitle1" fontWeight="bold">Säljinsikt just nu</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {getSalesInsights(data).map((insight, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box sx={{ fontSize: '1.1rem', flexShrink: 0, mt: 0.1 }}>{insight.icon}</Box>
                    <Typography variant="body2" sx={{ color: '#ddd', lineHeight: 1.5 }}>
                      {insight.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

/* ─── Data processing ─────────────────────────────────────────────────────── */
function processData(leads, plings, users, activities) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  // Monthly PLING/KLING (last 12 months)
  const monthlyPling = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    const nextD = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    const month = svMonth(d);
    const pling = plings.filter(p =>
      p.type === 'pling' && p.createdAt?.toDate?.() >= d && p.createdAt?.toDate?.() < nextD
    ).length;
    const kling = plings.filter(p =>
      p.type === 'kling-klang' && p.createdAt?.toDate?.() >= d && p.createdAt?.toDate?.() < nextD
    ).length;
    return { month, pling, kling };
  });

  // Pipeline funnel (active stages only)
  const activeStages = ['identified', 'qualified', 'demo', 'sandbox', 'proposal', 'negotiation', 'pilot'];
  const pipelineFunnel = STAGES
    .filter(s => activeStages.includes(s.key))
    .map(s => ({
      ...s,
      count: leads.filter(l => l.status === s.key).length,
      value: leads.filter(l => l.status === s.key).reduce((sum, l) => sum + (l.estimatedValue || 0), 0),
    }));

  // Win stats
  const wonLeads = leads.filter(l => l.status === 'won');
  const closedLeads = leads.filter(l => ['won', 'lost'].includes(l.status));
  const winRate = closedLeads.length > 0
    ? Math.round((wonLeads.length / closedLeads.length) * 100) : 0;
  const conversionRate = leads.length > 0
    ? Math.round((wonLeads.length / leads.length) * 100) : 0;
  const wonValue = wonLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0);
  const avgDealValue = wonLeads.length > 0 ? Math.round(wonValue / wonLeads.length) : 0;

  const activeLeads = leads.filter(l => activeStages.includes(l.status));
  const totalPipeline = activeLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0);

  // Monthly won value (last 12 months)
  const pipelineByMonth = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    const nextD = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    const won = leads
      .filter(l => l.status === 'won' && l.updatedAt?.toDate?.() >= d && l.updatedAt?.toDate?.() < nextD)
      .reduce((sum, l) => sum + (l.estimatedValue || 0), 0);
    return { month: svMonth(d), won };
  });

  // Activity breakdown
  const actCounts = {};
  activities.forEach(a => {
    actCounts[a.type] = (actCounts[a.type] || 0) + 1;
  });
  const activityBreakdown = Object.entries(actCounts).map(([key, val]) => ({
    name: ACTIVITY_LABELS[key] || key,
    value: val,
    color: ACTIVITY_COLORS[key] || '#999',
  })).sort((a, b) => b.value - a.value);

  // Top owners by won leads
  const ownerStats = {};
  wonLeads.forEach(l => {
    const u = users.find(u => u.id === l.ownerId);
    const name = u?.displayName || u?.email || 'Okänd';
    if (!ownerStats[name]) ownerStats[name] = { won: 0, value: 0 };
    ownerStats[name].won++;
    ownerStats[name].value += l.estimatedValue || 0;
  });
  const topOwners = Object.entries(ownerStats)
    .map(([name, s]) => ({ name, ...s }))
    .sort((a, b) => b.won - a.won)
    .slice(0, 5);

  const thisMonthPling = plings.filter(p => p.type === 'pling' && p.createdAt?.toDate?.() >= monthStart).length;
  const thisMonthKling = plings.filter(p => p.type === 'kling-klang' && p.createdAt?.toDate?.() >= monthStart).length;

  return {
    monthlyPling, pipelineFunnel, winRate, conversionRate,
    avgDealValue, pipelineByMonth, activityBreakdown, topOwners,
    totalPipeline, activeLeads: activeLeads.length,
    wonLeads: wonLeads.length, wonValue,
    thisMonthPling, thisMonthKling,
  };
}

function getSalesInsights(data) {
  const insights = [];
  if (!data) return insights;

  const { winRate, activeLeads, avgDealValue, activityBreakdown, pipelineFunnel, thisMonthPling } = data;

  if (winRate >= 50) {
    insights.push({ icon: '🔥', text: `Win rate på ${winRate}% — exceptionellt bra! Analysera vad som fungerar och gör mer av det.` });
  } else if (winRate > 0) {
    insights.push({ icon: '📈', text: `Win rate ${winRate}% — varje förbättring med 10% ökar intäkterna markant. Fokusera på kvalificering tidigt.` });
  }

  const proposalLeads = pipelineFunnel?.find(s => s.key === 'proposal')?.count || 0;
  if (proposalLeads > 0) {
    insights.push({ icon: '⏰', text: `${proposalLeads} leads väntar på svar efter offert. Kontakta dem idag — tid dödar affärer.` });
  }

  const topActivity = activityBreakdown?.[0];
  if (topActivity?.name === 'Notering') {
    insights.push({ icon: '📞', text: `Flest registrerade aktiviteter är noteringar. Boka fler samtal och möten — direktkontakt konverterar 3–5x bättre.` });
  }

  if (avgDealValue > 0) {
    insights.push({ icon: '💰', text: `Genomsnittlig dealstorlek: ${data.avgDealValue.toLocaleString('sv-SE')} kr. Identifiera kunderna med störst potential och investera mest tid där.` });
  }

  if (activeLeads > 20) {
    insights.push({ icon: '🎯', text: `${activeLeads} aktiva leads — risk för spridd uppmärksamhet. Prioritera top-5 leads varje vecka.` });
  } else if (activeLeads < 5) {
    insights.push({ icon: '🌱', text: 'Färre än 5 aktiva leads. Dags att prospektera aktivt — sikta på minst 10 leads i pipeline.' });
  }

  if (thisMonthPling === 0) {
    insights.push({ icon: '🚀', text: 'Inga PLING denna månad ännu. Varje kontakt är en potentiell PLING — gör ett samtal nu!' });
  } else {
    insights.push({ icon: '🔔', text: `${thisMonthPling} PLING denna månad! Bra momentum. Håll tempot uppe — konsekvent sälj slår intensiva perioder.` });
  }

  return insights.slice(0, 4);
}
